'use client';

import React, { useEffect, useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

// ─── Types ────────────────────────────────────────────────────────────────────
interface FormState {
  name: string;
  email: string;
  projectType: string;
  timeline: string;
  details: string;
}

// ─── Component ────────────────────────────────────────────────────────────────
const ContactPage: React.FC = () => {
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    projectType: '',
    timeline: '',
    details: '',
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form is for display only
  };

  // Scroll reveal
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.reveal-up').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const whyPoints = [
    'Fast turnaround — most projects delivered within 48–72 hours',
    'Unlimited revisions until you are 100% satisfied',
    'Experience across TikTok, YouTube, Instagram, and broadcast',
    'Clear communication and milestone-based workflow',
    'Available across all time zones — worldwide clients welcome',
  ];

  return (
    <div
      style={{
        background: '#0f0f0f',
        minHeight: '100vh',
        fontFamily: 'DM Sans, sans-serif',
        position: 'relative',
      }}
    >
      {/* Noise overlay */}
      <div className="noise-overlay" aria-hidden="true" />

      {/* Ambient blobs */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: '20%',
          right: '10%',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(220,38,38,0.07) 0%, transparent 70%)',
          pointerEvents: 'none',
          zIndex: 0,
          filter: 'blur(80px)',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          bottom: '10%',
          left: '5%',
          width: '350px',
          height: '350px',
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(220,38,38,0.05) 0%, transparent 70%)',
          pointerEvents: 'none',
          zIndex: 0,
          filter: 'blur(60px)',
        }}
      />

      <Header />

      {/* ── LIGHTWEIGHT HERO ──────────────────────────────────────────────── */}
      <section
        style={{
          position: 'relative',
          zIndex: 2,
          padding: '140px 24px 72px',
          textAlign: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        <span
          className="tag-pill reveal-up"
          style={{ marginBottom: '20px', display: 'inline-flex' }}
        >
          <span className="accent-dot" />
          Let&apos;s Collaborate
        </span>

        <h1
          className="reveal-up delay-1"
          style={{
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontSize: 'clamp(40px, 6vw, 72px)',
            fontWeight: 700,
            letterSpacing: '-0.03em',
            color: '#f5f5f5',
            margin: '16px 0 20px',
            lineHeight: 1.05,
          }}
        >
          Start Your Project
        </h1>

        <p
          className="reveal-up delay-2"
          style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '18px',
            fontWeight: 400,
            color: '#6b7280',
            maxWidth: '480px',
            margin: '0 auto',
            lineHeight: 1.6,
          }}
        >
          Tell me about your vision. I&apos;ll get back to you within 12 hours.
        </p>
      </section>

      {/* ── MAIN CONTACT LAYOUT ───────────────────────────────────────────── */}
      <main
        style={{
          position: 'relative',
          zIndex: 2,
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '80px 24px 120px',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '24px',
            alignItems: 'start',
          }}
        >
          {/* ── LEFT COLUMN ─────────────────────────────────────────────── */}
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
          >
            {/* Contact Information card */}
            <div
              className="glass-card reveal-up"
              style={{ padding: '32px' }}
            >
              <h2
                style={{
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontWeight: 700,
                  fontSize: '20px',
                  letterSpacing: '-0.02em',
                  color: '#f5f5f5',
                  margin: '0 0 28px',
                }}
              >
                Contact Information
              </h2>

              {/* Email */}
              <div className="contact-info-item">
                <div className="contact-icon-wrap">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fca5a5"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect width="20" height="16" x="2" y="4" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                </div>
                <div>
                  <div
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: '12px',
                      fontWeight: 500,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: '#4b5563',
                      marginBottom: '4px',
                    }}
                  >
                    Email
                  </div>
                  <a
                    href="mailto:personaledits.robert@gmail.com"
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: '15px',
                      color: '#fca5a5',
                      textDecoration: 'none',
                      fontWeight: 500,
                      transition: 'color 0.2s ease',
                    }}
                    onMouseEnter={(e) =>
                      ((e.currentTarget as HTMLElement).style.color = '#f5f5f5')
                    }
                    onMouseLeave={(e) =>
                      ((e.currentTarget as HTMLElement).style.color = '#fca5a5')
                    }
                  >
                    personaledits.robert@gmail.com
                  </a>
                </div>
              </div>

              {/* Discord */}
              <div className="contact-info-item">
                <div className="contact-icon-wrap">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="#fca5a5"
                    aria-hidden="true"
                  >
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.031.056a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                  </svg>
                </div>
                <div>
                  <div
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: '12px',
                      fontWeight: 500,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: '#4b5563',
                      marginBottom: '4px',
                    }}
                  >
                    Discord
                  </div>
                  <a
                    href="https://discord.com/users/calpz."
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: '15px',
                      color: '#fca5a5',
                      textDecoration: 'none',
                      fontWeight: 500,
                      transition: 'color 0.2s ease',
                    }}
                    onMouseEnter={(e) =>
                      ((e.currentTarget as HTMLElement).style.color = '#f5f5f5')
                    }
                    onMouseLeave={(e) =>
                      ((e.currentTarget as HTMLElement).style.color = '#fca5a5')
                    }
                  >
                    calpz.
                  </a>
                </div>
              </div>

              {/* Location */}
              <div className="contact-info-item">
                <div className="contact-icon-wrap">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fca5a5"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div>
                  <div
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: '12px',
                      fontWeight: 500,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: '#4b5563',
                      marginBottom: '4px',
                    }}
                  >
                    Location
                  </div>
                  <span
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: '15px',
                      color: '#9ca3af',
                      fontWeight: 400,
                    }}
                  >
                    Available Worldwide
                  </span>
                </div>
              </div>

              {/* Availability */}
              <div className="contact-info-item">
                <div className="contact-icon-wrap">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fca5a5"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div>
                  <div
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: '12px',
                      fontWeight: 500,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: '#4b5563',
                      marginBottom: '4px',
                    }}
                  >
                    Availability
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className="accent-dot" />
                    <span
                      style={{
                        fontFamily: 'DM Sans, sans-serif',
                        fontSize: '15px',
                        color: '#9ca3af',
                        fontWeight: 400,
                      }}
                    >
                      Open to new projects — March 2026
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Why Choose Me card */}
            <div
              className="glass-card reveal-up delay-1"
              style={{ padding: '32px' }}
            >
              <h2
                style={{
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontWeight: 700,
                  fontSize: '20px',
                  letterSpacing: '-0.02em',
                  color: '#f5f5f5',
                  margin: '0 0 24px',
                }}
              >
                Why Choose Me?
              </h2>

              <div>
                {whyPoints.map((point, i) => (
                  <div key={i} className="why-bullet">
                    <div className="why-bullet-dot" />
                    <p
                      style={{
                        fontFamily: 'DM Sans, sans-serif',
                        fontSize: '14px',
                        color: '#9ca3af',
                        margin: 0,
                        lineHeight: 1.6,
                      }}
                    >
                      {point}
                    </p>
                  </div>
                ))}
              </div>

              {/* Mini stats row */}
              <div
                style={{
                  marginTop: '28px',
                  paddingTop: '24px',
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '12px',
                }}
              >
                {[
                  { val: '20+', lbl: 'Projects' },
                  { val: '48hr', lbl: 'Turnaround' },
                  { val: '100%', lbl: 'Satisfaction' },
                ].map((s, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontFamily: 'Plus Jakarta Sans, sans-serif',
                        fontWeight: 800,
                        fontSize: '22px',
                        letterSpacing: '-0.03em',
                        color: '#fca5a5',
                        lineHeight: 1,
                      }}
                    >
                      {s.val}
                    </div>
                    <div
                      style={{
                        fontFamily: 'DM Sans, sans-serif',
                        fontSize: '11px',
                        fontWeight: 500,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: '#4b5563',
                        marginTop: '4px',
                      }}
                    >
                      {s.lbl}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── RIGHT COLUMN — Contact Form ──────────────────────────────── */}
          <div
            className="glass-card reveal-up delay-2"
            style={{ padding: '40px' }}
          >
            <form onSubmit={handleSubmit} noValidate>
              <h2
                style={{
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontWeight: 700,
                  fontSize: '24px',
                  letterSpacing: '-0.02em',
                  color: '#f5f5f5',
                  margin: '0 0 8px',
                }}
              >
                Send a Message
              </h2>
              <p
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '14px',
                  color: '#4b5563',
                  margin: '0 0 36px',
                }}
              >
                Fields marked with{' '}
                <span style={{ color: '#fca5a5' }}>*</span> are required.
              </p>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '20px',
                  marginBottom: '20px',
                }}
              >
                {/* Name */}
                <div>
                  <label
                    htmlFor="name"
                    style={{
                      display: 'block',
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: '13px',
                      fontWeight: 500,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: '#6b7280',
                      marginBottom: '8px',
                    }}
                  >
                    Name <span style={{ color: '#fca5a5' }}>*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    style={{
                      display: 'block',
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: '13px',
                      fontWeight: 500,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: '#6b7280',
                      marginBottom: '8px',
                    }}
                  >
                    Email <span style={{ color: '#fca5a5' }}>*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Project Type */}
              <div style={{ marginBottom: '20px' }}>
                <label
                  htmlFor="projectType"
                  style={{
                    display: 'block',
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '13px',
                    fontWeight: 500,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: '#6b7280',
                    marginBottom: '8px',
                  }}
                >
                  Project Type
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={form.projectType}
                  onChange={handleChange}
                  className="form-input"
                  style={{ appearance: 'none', cursor: 'pointer' }}
                >
                  <option value="">Select a project type…</option>
                  <option value="showreel">Showreel / Highlight Reel</option>
                  <option value="longform">Long Form (YouTube / Documentary)</option>
                  <option value="shortform">Short Form (TikTok / Reels / Shorts)</option>
                  <option value="commercial">Commercial / Brand Video</option>
                  <option value="musicvideo">Music Video</option>
                  <option value="event">Event Coverage</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Timeline */}
              <div style={{ marginBottom: '20px' }}>
                <label
                  htmlFor="timeline"
                  style={{
                    display: 'block',
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '13px',
                    fontWeight: 500,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: '#6b7280',
                    marginBottom: '8px',
                  }}
                >
                  Timeline
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  value={form.timeline}
                  onChange={handleChange}
                  className="form-input"
                  style={{ appearance: 'none', cursor: 'pointer' }}
                >
                  <option value="">How soon do you need this?</option>
                  <option value="asap">ASAP (Rush — within 24hrs)</option>
                  <option value="2-3days">2–3 days</option>
                  <option value="1week">Within 1 week</option>
                  <option value="2weeks">Within 2 weeks</option>
                  <option value="1month">Within a month</option>
                  <option value="flexible">Flexible / No deadline</option>
                </select>
              </div>

              {/* Project Details */}
              <div style={{ marginBottom: '32px' }}>
                <label
                  htmlFor="details"
                  style={{
                    display: 'block',
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '13px',
                    fontWeight: 500,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: '#6b7280',
                    marginBottom: '8px',
                  }}
                >
                  Project Details{' '}
                  <span style={{ color: '#fca5a5' }}>*</span>
                </label>
                <textarea
                  id="details"
                  name="details"
                  rows={6}
                  placeholder="Tell me about your project — style references, goals, footage details, any other context…"
                  value={form.details}
                  onChange={handleChange}
                  className="form-input"
                  style={{ resize: 'vertical', minHeight: '140px' }}
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: '18px 24px',
                  background: '#f5f5f5',
                  border: 'none',
                  borderRadius: '14px',
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontWeight: 700,
                  fontSize: '16px',
                  color: '#0f0f0f',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease',
                  letterSpacing: '-0.01em',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow =
                    '0 0 40px rgba(245, 245, 245, 0.2)';
                  (e.currentTarget as HTMLElement).style.transform =
                    'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                  (e.currentTarget as HTMLElement).style.transform =
                    'translateY(0)';
                }}
              >
                Send Message
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#0f0f0f"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="m22 2-7 20-4-9-9-4Z" />
                  <path d="M22 2 11 13" />
                </svg>
              </button>

              <p
                style={{
                  marginTop: '16px',
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '12px',
                  color: '#374151',
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                By submitting, you agree to be contacted regarding your
                project. No spam, ever.
              </p>
            </form>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;